import { Button } from '@material-ui/core';
import { ButtonProps } from '@material-ui/core/Button/Button';
import clsx from 'clsx';
import { ReactComponent as ArrowLeftIcon } from 'common-assets/assets/icons/arrow-left.svg';
import { ReactComponent as ArrowRightIcon } from 'common-assets/assets/icons/arrow-right.svg';
import { ReactComponent as ChevronDownIcon } from 'common-assets/assets/icons/chevron-down.svg';
import { ReactComponent as ChevronLeftIcon } from 'common-assets/assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from 'common-assets/assets/icons/chevron-right.svg';
import { ReactComponent as ChevronUpIcon } from 'common-assets/assets/icons/chevron-up.svg';
import { ReactComponent as SpinnerIcon } from 'common-assets/assets/icons/spinner-icon.svg';
import React, { forwardRef, MouseEventHandler } from 'react';
import { ExternalLinkWcagAttributes } from '../wcag/wcagAttributes';
import { mergeRefs, useMeasure } from 'common-react';
import { checkIfButtonHasEnoughSpaceForSpinner } from './tools/Button.utils';

import useStyles from './Button.style';

export type IconVariant = 'chevronRight' | 'chevronLeft' | 'arrowRight' | 'arrowLeft' | 'chevronDown' | 'chevronUp';

const ICON_MAP: Record<IconVariant, React.FC<React.SVGProps<SVGSVGElement>>> = {
  chevronRight: ChevronRightIcon,
  chevronLeft: ChevronLeftIcon,
  arrowRight: ArrowRightIcon,
  arrowLeft: ArrowLeftIcon,
  chevronDown: ChevronDownIcon,
  chevronUp: ChevronUpIcon,
};

type BaseButtonProps = Omit<ButtonProps, 'href'> & {
  buttonType?: "primary" | "secondary" | "tertiary";
  buttonVariant?: "large" | "medium" | "small";
  accent?: boolean;
  icon?: IconVariant;
  iconPosition?: 'before' | 'after';
  customIcon?: React.ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  className?: string;
  targetBlank?: boolean;
  isLoading?: boolean;
  error?: string;
  width?: string;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  testId?: string;
  autoTestId?: string;
  disableWcagAttributes?: boolean;
  component?: React.ElementType;
  disableFullWidthOnMobile?: boolean;
};

export type CustomButtonProps = BaseButtonProps & (
  | { href?: string; });

const getIconComponent = (iconVariant?: IconVariant, isLoading?: boolean, iconPosition?: 'before' | 'after'): React.FC<React.SVGProps<SVGSVGElement>> | null => {
  if (isLoading && iconPosition === 'after' && iconVariant) {
    return SpinnerIcon;
  }
  return iconVariant ? ICON_MAP[iconVariant] : null;
}

const CustomButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  CustomButtonProps
>(
  (
    {
      isLoading = false,
      error,
      buttonType = 'primary',
      buttonVariant = 'large',
      accent = false,
      icon,
      iconPosition = 'after',
      customIcon,
      href = "#",
      className,
      onClick,
      targetBlank,
      width = '120px',
      ariaLabel,
      ariaExpanded,
      testId,
      autoTestId,
      disableWcagAttributes,
      disableFullWidthOnMobile = false,
      ...props
    },
    ref
  ) => {

    const renderAsLink = href !== undefined && href !== "#";
    const IconComponent = getIconComponent(icon, isLoading, iconPosition);
    const [buttonRef, { width: buttonWidth }] = useMeasure({ disableOnCondition: !isLoading });
    const [buttonLabelRef, { width: buttonLabelWidth }] = useMeasure({ disableOnCondition: !isLoading });
    const isIconDefined = IconComponent != null || customIcon != null;
    const hasIconInRightPosition = iconPosition === 'after' && isIconDefined;
    const hasIconInLeftPosition = iconPosition === 'before' && isIconDefined;
    const hasEnoughSpaceForSpinner = isLoading
      ? checkIfButtonHasEnoughSpaceForSpinner(
          buttonWidth as number,
          buttonLabelWidth as number,
          buttonVariant,
          hasIconInLeftPosition,
          hasIconInRightPosition,
        )
      : true;
      const c = useStyles({
      accent,
      link: renderAsLink,
      width,
      buttonType,
      buttonVariant,
      hasEnoughSpaceForSpinner,
      isLoading,
      disableFullWidthOnMobile,
    });
    const linkHref = href ?? "#";
    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick(e as any);
      }
    };

    const linkAttributes = targetBlank && !disableWcagAttributes
      ? ExternalLinkWcagAttributes
      : {rel: 'noopener noreferrer'};

    const iconElement = customIcon ?? (
      IconComponent ? (
        <IconComponent
          className={clsx({
            [c.icon]: true,
            [c.iconLeft]: hasIconInLeftPosition,
            [c.iconSpinner]: isLoading,
          })}
          data-testid={icon ? `${icon}-icon` : undefined}
        />
      ) : null
    );

    const buttonContent = (
      <span className={c.buttonLabel}>
        {iconPosition === 'before' && iconElement}
        <span ref={buttonLabelRef} className={c.buttonLabelText}>{props.children}</span>
        {iconPosition === 'after' && iconElement}
        {isLoading && !hasIconInRightPosition && <SpinnerIcon className={c.spinner} />}
      </span>
    );

    return (
      <>
        {renderAsLink ? (
          <a
            ref={mergeRefs(ref, buttonRef)}
            href={linkHref}
            {...linkAttributes}
            className={clsx(c.root, className)}
            onClick={handleLinkClick}
            aria-label={ariaLabel}
            aria-expanded={ariaExpanded}
            data-testid={testId}
            data-autotestid={autoTestId}
          >
            {buttonContent}
          </a>
        ) : (
          <Button
            {...props}
            aria-label={ariaLabel}
            aria-expanded={ariaExpanded}
            ref={mergeRefs(ref, buttonRef)}
            onClick={onClick}
            classes={{ root: clsx(c.root, className) }}
            disabled={isLoading || props.disabled}
            data-testid={testId}
            data-autotestid={autoTestId}
          >
            {buttonContent}
          </Button>
        )}
      </>
    );
  }
);

CustomButton.displayName = "CustomButton";

const CustomUiComponentsButton = CustomButton as (
    props: CustomButtonProps & React.RefAttributes<HTMLButtonElement | HTMLAnchorElement>
) => React.ReactElement | null;

export { CustomUiComponentsButton as Button };
